import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Constants } from 'src/app/app.constants';
import { MerchantService } from 'src/app/services/merchant.service';
import { AuthService } from 'src/app/services';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'microsite-config-dialog',
  templateUrl: './microsite-config-dialog.component.html',
  styleUrls: ['./microsite-config-dialog.component.css'],
})
export class MicrositeConfigDialogComponent implements OnInit {
  form!: FormGroup;
  saving = false;
  merchant: any;
  micrositeUrl = '';
  initialLogoUrl?: string | null;
  initialCoverUrl?: string | null;
  initialEmailArtworkUrl?: string | null;
  uploadedLogo?: any;
  uploadedCover?: any;
  uploadedEmailArtwork?: any;
  currentLogoUrl?: string | null;
  currentCoverUrl?: string | null;
  currentEmailArtworkUrl?: string | null;
  countries: any[] = [];
  countryFormControl!: FormControl;

  // Add this enum for edge types
  edgeTypes = [
    { value: 'square', label: 'Square' },
    { value: 'rounded', label: 'Rounded' },
  ];

  constructor(
    public constants: Constants,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<MicrositeConfigDialogComponent>,
    private fb: FormBuilder,
    private merchantService: MerchantService,
    private authService: AuthService,
    private http: HttpClient,
  ) {
    this.merchant = this.authService.getNestedUserProperty(
      'merchant',
      undefined,
    );
    this.initializeForm();
  }

  ngOnInit() {
    this.updateMicrositeUrl();
    this.loadInitialImages();
    this.loadCountries();

    // Watch for subdomain changes to update the URL preview
    this.form.get('subdomain')?.valueChanges.subscribe((subdomain) => {
      if (subdomain) {
        this.micrositeUrl = `https://${subdomain}.remyrewards.co.uk`;
      } else {
        this.micrositeUrl = '';
      }
    });
  }

  private loadCountries(): void {
    this.http
      .get<any>('../../../../assets/json/countries.json')
      .subscribe((data) => {
        this.countries = data;

        const microsite = this.merchant?.microsite || {};
        const branding = microsite.branding || {};
        const contact = branding.contact || {};
        const existingCountry = this.countries?.find(
          (el: any) => el.name === contact.address?.country,
        );

        this.countryFormControl = new FormControl(existingCountry || null);

        // Add the country form control to the form
        this.form.addControl('addressCountryObj', this.countryFormControl);
      });
  }

  private initializeForm(): void {
    const microsite = this.merchant?.microsite || {};
    const branding = microsite.branding || {};
    const colors = branding.colors || {};
    const contact = branding.contact || {};
    const settings = branding.settings || {};

    // Determine edge type based on radius value
    const edgeType =
      settings.radius && settings.radius > 0 ? 'rounded' : 'square';

    this.form = this.fb.group({
      enabled: [microsite.enabled || false],
      subdomain: [
        microsite.subdomain || '',
        [
          Validators.required,
          Validators.pattern(/^[a-z]+$/),
          Validators.minLength(3),
          Validators.maxLength(20),
        ],
      ],
      primaryColor: [colors.primary || '#000000'],
      secondaryColor: [colors.secondary || '#ffffff'],
      tertiaryColor: [colors.tertiary || '#f0f0f0'],
      backgroundColor: [colors.background || '#ffffff'],
      textColor: [colors.text || '#000000'],
      buttonColor: [colors.button || '#000000'],
      buttonTextColor: [colors.buttonText || '#ffffff'],
      discountBadgeColor: [colors.discount || '#ff4444'],
      discountBadgeTextColor: [colors.discountText || '#ffffff'],
      edgeType: [edgeType],
      phone: [contact.phone || ''],
      email: [contact.email || '', Validators.email],
      website: [contact.website || '', Validators.required],
      addressLine1: [contact.address?.line1 || ''],
      addressLine2: [contact.address?.line2 || ''],
      addressCity: [contact.address?.city || ''],
      addressPostalCode: [contact.address?.postalCode || ''],
      title: [settings.title || 'Gift Vouchers', Validators.required],
      googleAnalyticsId: [settings.googleAnalyticsId || ''],
    });

    // Disable subdomain field if microsite is approved
    if (microsite.approved) {
      this.form.get('subdomain')?.disable();
    }
  }

  private updateMicrositeUrl(): void {
    const subdomain = this.merchant?.microsite?.subdomain;
    if (subdomain) {
      this.micrositeUrl = `https://${subdomain}.remyrewards.co.uk`;
    }
  }

  private loadInitialImages(): void {
    const microsite = this.merchant?.microsite || {};
    const branding = microsite.branding || {};

    // Set initial logo URL if it exists
    if (branding.logo?.url) {
      this.initialLogoUrl = branding.logo.url + '?' + new Date().getTime();
      this.currentLogoUrl = this.initialLogoUrl;
    }

    // Set initial cover URL if it exists
    if (branding.cover?.url) {
      this.initialCoverUrl = branding.cover.url;
      this.currentCoverUrl = this.initialCoverUrl;
    }

    if (branding.emailArtwork?.url) {
      this.initialEmailArtworkUrl = branding.emailArtwork.url;
      this.currentEmailArtworkUrl = this.initialEmailArtworkUrl;
    }
  }

  get isApproved(): boolean {
    return this.merchant?.microsite?.approved || false;
  }

  get approvalStatus(): string {
    if (this.isApproved) {
      return 'Approved';
    }
    return this.merchant?.microsite?.enabled
      ? 'Pending Approval'
      : 'Not Submitted';
  }

  get statusColor(): string {
    if (this.isApproved) {
      return 'green';
    }
    return this.merchant?.microsite?.enabled ? 'orange' : 'gray';
  }

  onLogoSelected(files: any[]): void {
    if (files && files.length > 0) {
      // Store the uploaded logo file
      this.uploadedLogo = files[0];
      // Update the current URL to show the new image preview
      this.currentLogoUrl = files[0].content;
    }
  }

  onCoverSelected(files: any[]): void {
    if (files && files.length > 0) {
      // Store the uploaded cover file
      this.uploadedCover = files[0];
      // Update the current URL to show the new image preview
      this.currentCoverUrl = files[0].content;
    }
  }

  onEmailArtworkSelected(files: any[]): void {
    if (files && files.length > 0) {
      this.uploadedEmailArtwork = files[0];
      this.currentEmailArtworkUrl = files[0].content;
    }
  }

  copyToClipboard(): void {
    if (this.micrositeUrl) {
      navigator.clipboard.writeText(this.micrositeUrl).then(() => {
        this.constants.snack('Microsite URL copied to clipboard');
      });
    }
  }

  openInNewTab(): void {
    if (this.micrositeUrl) {
      window.open(this.micrositeUrl, '_blank');
    }
  }

  // Helper method to get radius value based on edge type
  getRadiusValue(): number {
    return this.form.get('edgeType')?.value === 'rounded' ? 10 : 0;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const formValue = this.form.value;

    const payload: any = {
      enabled: formValue.enabled,
      branding: {
        colors: {
          primary: formValue.primaryColor,
          secondary: formValue.secondaryColor,
          tertiary: formValue.tertiaryColor,
          background: formValue.backgroundColor,
          text: formValue.textColor,
          button: formValue.buttonColor,
          buttonText: formValue.buttonTextColor,
          discount: formValue.discountBadgeColor,
          discountText: formValue.discountBadgeTextColor,
        },
        contact: {
          phone: formValue.phone,
          email: formValue.email,
          website: formValue.website,
          address: {
            line1: formValue.addressLine1,
            line2: formValue.addressLine2,
            city: formValue.addressCity,
            postalCode: formValue.addressPostalCode,
            country: formValue.addressCountryObj?.name || '',
          },
        },
        settings: {
          radius: this.getRadiusValue(),
          font: formValue.font?.value || formValue.font,
          title: formValue.title,
          googleAnalyticsId: formValue.googleAnalyticsId,
        },
      },
    };

    // Only include subdomain if not approved (field is enabled)
    if (!this.isApproved && formValue.subdomain) {
      payload.subdomain = formValue.subdomain;
    }

    // Add uploaded files to payload if they exist
    if (this.uploadedLogo) {
      payload.logo = this.uploadedLogo;
    }
    if (this.uploadedCover) {
      payload.cover = this.uploadedCover;
    }
    if (this.uploadedEmailArtwork) {
      payload.emailArtwork = this.uploadedEmailArtwork;
    }

    this.merchantService.updateMicrositeConfig(payload).subscribe({
      next: () => {
        this.matDialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.constants.snack(
          error.error?.message || 'Error saving configuration',
        );
      },
      complete: () => {
        this.saving = false;
      },
    });
  }

  close(): void {
    this.matDialogRef.close();
  }

  get fontFormControl(): FormControl {
    return this.form.get('font') as FormControl;
  }
}
